import { Card, CardContent } from "../../ui/card"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs"
import { SummaryHeader } from "../../ui/SummaryHeader"
import { useChartColors } from "../../../hooks/useChartColors"
import SalesLoader from "../../../components/loaders/SalesLoader"
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useMemo } from "react"
import { useWholesaleSales } from "../hooks/useSalesWholesale";
import { DataTable } from "../../ui/data-table/data-table"
import { useWholesaleDataCheck } from "../hooks/useWholesaleDataCheck";
import { formatNumber } from "../../../utils/formatNumber"
import { formatCurrency } from "../../../utils/formatCurrency"


interface CategoryPerformanceProps {
  selectedMonth: string;
}


interface CategoryPerformanceMetrics {
  category: string;
  revenue: number;
  averagePrice: number;
  units: number;
  averageOrderValue: number;
}

export function WholesaleCategoryPerformance({ selectedMonth }: CategoryPerformanceProps) {

  const monthDate = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    return new Date(year, month - 1, 1);
  }, [selectedMonth]);

  const startDate = format(startOfMonth(monthDate), "yyyy-MM-dd");
  const endDate = format(endOfMonth(monthDate), "yyyy-MM-dd");


  const { data, isLoading } = useWholesaleSales(startDate, endDate);

  const wholesaleData: CategoryPerformanceMetrics[] = useMemo(
    () =>
      (data?.wholesale_category_performance || []).map((cat) => ({
        category: cat.category,
        revenue: cat.revenue,
        averagePrice: cat.average_price,
        units: cat.units_sold,
        averageOrderValue: cat.average_order,
      })),
    [data]
  );

  // Use the combined data check hook
  const { hasAnyData } = useWholesaleDataCheck(wholesaleData);

  

  const columns = [
    {
      accessorKey: "category",
      header: "Category",
      cell: (info: any) => info.getValue(),
    },
    {
      accessorKey: "averageOrderValue",
      header: "Avg. Order",
      cell: (info: any) => formatNumber(info.getValue()),
      isNumber: true,
    },
    {
      accessorKey: "averagePrice",
      header: "Avg. Price",
      cell: (info: any) => formatCurrency(info.getValue()),
      isNumber: true,
    },
    {
      accessorKey: "units",
      header: "Units Sold",
      cell: (info: any) => formatNumber(info.getValue()),
      isNumber: true,
    },
    {
      accessorKey: "revenue",
      header: "Revenue",
      cell: (info: any) => formatCurrency(info.getValue()),
      isNumber: true,
    },
  ];

  const colors = useChartColors("wholesale");
  if (!colors) return null;

  return (
    <>
      <SummaryHeader title="General" />
      <Card>
        <CardContent>
          <Tabs defaultValue="chart" className="space-y-4">
            <TabsList className="flex flex-wrap gap-1">
              <TabsTrigger value="chart">Chart</TabsTrigger>
              <TabsTrigger value="pie">Distribution</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
            <TabsContent value="chart" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              {isLoading ? (
                <SalesLoader variant="performance-bar" />
              ) : !hasAnyData ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No Data Available
                </div>
              ) : (
                <><ResponsiveContainer width="100%" height={350}>
                  <BarChart data={wholesaleData} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value).replace(/[^0-9.KMB]/g, "")} />
                    <YAxis type="category" dataKey="category" width={110} interval={0} />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: "0.875rem",
                      }}
                      itemStyle={{
                        color: "var(--foreground)",
                      }} />

                    <Bar dataKey="revenue" name="Revenue">
                      {wholesaleData.map((_entry, index) => (
                        <Cell key={`bar-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer><div className="flex justify-center gap-4 mt-4 px-2 flex-wrap">
                    {wholesaleData.map((entry, index) => (
                      <div key={entry.category} className="flex items-center gap-2 text-sm">
                        <span
                          className="w-3 h-3 rounded-sm inline-block"
                          style={{ backgroundColor: colors[index % colors.length] }} />
                        <span>{entry.category}</span>
                      </div>
                    ))}
                  </div></>
              )}
            </TabsContent>
            <TabsContent value="pie" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              {isLoading ? (
                <SalesLoader variant="performance-pie" />
              ) : !hasAnyData ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No Data Available
                </div>) : (
                <><ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={wholesaleData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill={colors[0]}
                      dataKey="revenue"
                      nameKey="category"
                    >
                      {wholesaleData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      contentStyle={{
                        backgroundColor: "var(--background)",
                        borderColor: "var(--border)",
                        borderRadius: "var(--radius)",
                        fontSize: "0.875rem",
                      }}
                      itemStyle={{
                        color: "var(--foreground)",
                      }} />
                  </PieChart>
                </ResponsiveContainer>
                  <div className="mt-4 flex flex-wrap justify-center gap-4">
                    {wholesaleData.map((entry, index) => {
                      const total = wholesaleData.reduce((sum, d) => sum + d.revenue, 0)
                      const percent = total ? (entry.revenue / total) * 100 : 0
                      return (
                        <div key={entry.category} className="flex items-center gap-2 text-sm">
                          <div
                            className="w-3 h-3 rounded-sm"
                            style={{ backgroundColor: colors[index % colors.length] }} />
                          <span>{entry.category}: {percent.toFixed(2)}%</span>
                        </div>
                      )
                    })}
                  </div></>
              )}
            </TabsContent>
            <TabsContent value="table" className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              <div className="rounded-md border max-h-[550px] overflow-auto p-4">
                {isLoading ? (
                  <SalesLoader variant="performance-channel" />
                ) : !hasAnyData ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No Data Available
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={wholesaleData}
                    searchColumn="category"
                    searchPlaceholder="Search by Category..."
                    showRowsPerPage={false}
                    initialPageSize={5}
                    showTotal={true} // Enable totals row
                    totalColumns={["revenue"]} // Columns to total
                  />
                )}      
                </div>  
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </>
  )
}
